import type { PublicDocId } from '@/lib/api';

// Defines the feedback vocabulary: the scenes, targets, tags, and breadcrumb-event kinds
// feedback can express, plus the shapes that carry them. Field names are snake_case because
// a FeedbackSubmission is serialized to JSON when it is sent to the backend.

/** Enumerates the scene identifiers shared across the feedback ontology. */
export const FEEDBACK_SCENE_KIND = {
  DOC_THREAD: 'doc_thread',
  HOME_FEED: 'home_feed',
  PRODUCT_PAGE: 'product_page',
  GUIDE_PAGE: 'guide_page',
  UNKNOWN: 'unknown',
} as const;

/** Describes the allowed scene identifiers. */
export type FeedbackSceneKind = (typeof FEEDBACK_SCENE_KIND)[keyof typeof FEEDBACK_SCENE_KIND];

/** Enumerates the home-feed endpoint identifiers used by feedback scenes. */
export const FEEDBACK_HOME_FEED_ENDPOINT = {
  PUBLIC: 'public',
  UNIFIED: 'unified',
} as const;

/** Describes the allowed home-feed endpoint identifiers. */
export type FeedbackHomeFeedEndpoint =
  (typeof FEEDBACK_HOME_FEED_ENDPOINT)[keyof typeof FEEDBACK_HOME_FEED_ENDPOINT];

/** Enumerates the target identifiers shared across the feedback ontology. */
export const FEEDBACK_TARGET_KIND = {
  DOC: 'doc',
  FEED_ITEM: 'feed_item',
  PRODUCT: 'product',
  TOPIC: 'topic',
} as const;

/** Describes the allowed target identifiers. */
export type FeedbackTargetKind = (typeof FEEDBACK_TARGET_KIND)[keyof typeof FEEDBACK_TARGET_KIND];

/** Enumerates the breadcrumb event identifiers shared across the feedback ontology. */
export const FEEDBACK_BREADCRUMB_KIND = {
  NAVIGATION: 'navigation',
  ACTION: 'action',
  NOTIFICATION: 'notification',
  ERROR: 'error',
} as const;

/** Describes the allowed breadcrumb event identifiers. */
export type FeedbackBreadcrumbKind =
  (typeof FEEDBACK_BREADCRUMB_KIND)[keyof typeof FEEDBACK_BREADCRUMB_KIND];

/** Enumerates the action outcomes shared across the feedback ontology. */
export const BREADCRUMB_OUTCOME = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILURE: 'failure',
} as const;

/** Describes the allowed action outcome identifiers. */
export type BreadcrumbOutcome = (typeof BREADCRUMB_OUTCOME)[keyof typeof BREADCRUMB_OUTCOME];

/** Enumerates the action vocabulary feedback can express. */
export const FEEDBACK_ACTION = {
  POST_MESSAGE: 'post_message',
  POST_QUESTION: 'post_question',
  POST_SUGGESTED_QUESTION: 'post_suggested_question',
  REQUEST_AI_REPLY: 'request_ai_reply',
  UPVOTE: 'upvote',
  DOWNVOTE: 'downvote',
  RETRACT_VOTE: 'retract_vote',
  SAVE_POST: 'save_post',
  UNSAVE_POST: 'unsave_post',
  SAVE_PRODUCT: 'save_product',
  UNSAVE_PRODUCT: 'unsave_product',
  ADD_PRODUCT_BY_URL: 'add_product_by_url',
  FOLLOW_USER: 'follow_user',
  UNFOLLOW_USER: 'unfollow_user',
  FOLLOW_TOPIC: 'follow_topic',
  UNFOLLOW_TOPIC: 'unfollow_topic',
  CREATE_INVITE: 'create_invite',
  LOG_IN: 'log_in',
  CREATE_ACCOUNT: 'create_account',
  START_OAUTH_SIGN_IN: 'start_oauth_sign_in',
  COMPLETE_OAUTH_SIGN_IN: 'complete_oauth_sign_in',
  LOG_OUT: 'log_out',
  CHANGE_PASSWORD: 'change_password',
  START_EMAIL_CHANGE: 'start_email_change',
  RESEND_EMAIL_VERIFICATION: 'resend_email_verification',
  CANCEL_EMAIL_CHANGE: 'cancel_email_change',
  REQUEST_PASSWORD_RESET: 'request_password_reset',
  RESET_PASSWORD: 'reset_password',
  UPDATE_AVATAR: 'update_avatar',
  UPDATE_MARKETPLACE_PREFERENCES: 'update_marketplace_preferences',
  UPDATE_PROFILE_DISCOVERABILITY: 'update_profile_discoverability',
  REMOVE_OAUTH_CONNECTION: 'remove_oauth_connection',
  DELETE_ACCOUNT: 'delete_account',
} as const;

/** Describes the allowed feedback action identifiers. */
export type FeedbackAction = (typeof FEEDBACK_ACTION)[keyof typeof FEEDBACK_ACTION];

/** Enumerates the UI surfaces that originate an action breadcrumb (`detail.source`). */
export const FEEDBACK_SOURCE = {
  LIVE_CONTEXT: 'live_context',
  PRODUCT_MINI_CARD: 'product_mini_card',
  PRODUCT_PAGE: 'product_page',
  FOLLOW_BUTTON_DIRECT: 'follow_button_direct',
  FOLLOW_BUTTON_MODAL: 'follow_button_modal',
} as const;

/** Describes the allowed action-source identifiers. */
export type FeedbackSource = (typeof FEEDBACK_SOURCE)[keyof typeof FEEDBACK_SOURCE];

/**
 * Describes the bounded, PII-free detail payload carried by an action breadcrumb.
 *
 * This is the ontology-owned contract for `Breadcrumb.detail` on action events: the
 * breadcrumb helpers stamp `action`, and callers may attach the known structured keys
 * below rather than arbitrary records, so the shape stays consistent across producers.
 */
export type ActionDetail = {
  /** Identifies the action contract the breadcrumb describes; stamped by the breadcrumb helpers. */
  action?: FeedbackAction;
  /** Identifies the UI surface that originated the action. */
  source?: FeedbackSource;
  /** Identifies the OAuth provider involved in the action, when applicable. */
  provider?: string;
  /** Stores a bounded, PII-free failure reason for a failed action. */
  error?: string;
};

/** Describes the product surface the user was on when they opened feedback. */
export type Scene =
  | {
      /** Identifies the scene as a document thread view. */
      kind: typeof FEEDBACK_SCENE_KIND.DOC_THREAD;
      /** Stores the public thread identifier shown in the doc route. */
      thread_id: PublicDocId;
      /** Stores the resolved main topic when known for the thread. */
      main_topic: string | null;
    }
  | {
      /** Identifies the scene as the home feed. */
      kind: typeof FEEDBACK_SCENE_KIND.HOME_FEED;
      /** Distinguishes the anonymous and authenticated home feed variants. */
      endpoint: FeedbackHomeFeedEndpoint;
    }
  | {
      /** Identifies the scene as a product page. */
      kind: typeof FEEDBACK_SCENE_KIND.PRODUCT_PAGE;
      /** Stores the product reference derived from the route. */
      product_ref: string;
    }
  | {
      /** Identifies the scene as a guide page. */
      kind: typeof FEEDBACK_SCENE_KIND.GUIDE_PAGE;
      /** Stores the guide topic key derived from the route. */
      topic_key: string;
    }
  | {
      /** Identifies scenes that do not match a tracked surface. */
      kind: typeof FEEDBACK_SCENE_KIND.UNKNOWN;
    };

/** Describes the specific entity the user targeted when opening feedback. */
export type Target =
  | {
      /** Identifies the target as a document thread. */
      kind: typeof FEEDBACK_TARGET_KIND.DOC;
      /** Stores the targeted document thread identifier. */
      id: PublicDocId;
    }
  | {
      /** Identifies the target as a feed item. */
      kind: typeof FEEDBACK_TARGET_KIND.FEED_ITEM;
      /** Stores the targeted feed item identifier. */
      id: PublicDocId;
    }
  | {
      /** Identifies the target as a product page. */
      kind: typeof FEEDBACK_TARGET_KIND.PRODUCT;
      /** Stores the targeted product reference. */
      product_ref: string;
    }
  | {
      /** Identifies the target as a guide topic. */
      kind: typeof FEEDBACK_TARGET_KIND.TOPIC;
      /** Stores the targeted guide topic key. */
      topic_key: string;
    }
  | null;

/** Records a breadcrumb event captured before the user submitted feedback. */
export type Breadcrumb = {
  /** Stores the stable client-local identifier assigned when the breadcrumb is created. */
  id: string;
  /** Stores the event timestamp in milliseconds since epoch. */
  ts: number;
  /** Identifies the breadcrumb category used by feedback consumers. */
  kind: FeedbackBreadcrumbKind;
  /** Stores the short label shown for the breadcrumb event. */
  label: string;
  /** Stores the action outcome when the breadcrumb describes an in-flight or completed action. */
  outcome?: BreadcrumbOutcome;
  /** Stores optional structured details for richer breadcrumb events. */
  detail?: Record<string, unknown>;
};

/** Enumerates the trigger identifiers shared across the feedback ontology. */
export const FEEDBACK_TRIGGERS = {
  DOWNVOTE_AUTO: 'downvote_auto',
  FLAG: 'flag',
  MENU: 'menu',
} as const;

/** Describes the allowed trigger identifiers. */
export type FeedbackTrigger = (typeof FEEDBACK_TRIGGERS)[keyof typeof FEEDBACK_TRIGGERS];

/** Defines the full client-side feedback payload submitted by the form. */
export type FeedbackSubmission = {
  /** Stores how the feedback flow was opened. */
  trigger: FeedbackTrigger;
  /** Stores the tags the user explicitly selected. */
  tags: string[];
  /** Stores offered tags the user left unselected. */
  unselected_tags: string[];
  /** Stores the freeform message entered by the user. */
  message: string;
  /** Stores the scene captured when feedback was opened. */
  scene: Scene;
  /** Stores the optional entity the user targeted when opening feedback. */
  target: Target;
  /** Stores the breadcrumb trail captured before submission. */
  breadcrumbs: Breadcrumb[];
  /** Stores the sanitized route captured from the browser location. */
  route: {
    /** Stores the browser pathname at capture time. */
    pathname: string;
    /** Stores the filtered query string preserved for feedback capture. */
    search: string;
  };
  /** Stores browser environment fields attached to the submission payload. */
  env: {
    /** Stores the current browser user agent string. */
    user_agent: string;
    /** Stores the viewport width at capture time. */
    viewport_w: number;
    /** Stores the viewport height at capture time. */
    viewport_h: number;
    /** Stores the active theme mode seen by the user. */
    color_scheme: 'light' | 'dark';
  };
};

/** Defines the application-level status codes returned by feedback submission. */
export type FeedbackSubmitStatus = 'success';

/** Defines the minimal response body returned by feedback submission. */
export type FeedbackSubmitResponse = {
  /** Stores the application-level outcome for the submission request. */
  status: FeedbackSubmitStatus;
};

/** Fails exhaustive unions when a caller reaches an unsupported variant. */
export function assertNever(x: never): never {
  throw new Error('Unexpected value: ' + JSON.stringify(x));
}

/** Builds a document-thread scene for the given public thread identifier. */
export function sceneDocThread(thread_id: PublicDocId, main_topic: string | null): Scene {
  return { kind: FEEDBACK_SCENE_KIND.DOC_THREAD, thread_id, main_topic };
}

/** Builds a home-feed scene for the current authenticated or public endpoint. */
export function sceneHomeFeed(endpoint: FeedbackHomeFeedEndpoint): Scene {
  return { kind: FEEDBACK_SCENE_KIND.HOME_FEED, endpoint };
}

/** Builds a product-page scene for the given product reference. */
export function sceneProductPage(product_ref: string): Scene {
  return { kind: FEEDBACK_SCENE_KIND.PRODUCT_PAGE, product_ref };
}

/**
 * Builds the guide-page feedback scene from the route topic.
 *
 * `topic_key` is the raw decoded route topic (what the user navigated to), not a
 * resolved/canonical concept key. A feedback scene only needs immediate, stable
 * context for a report; resolving to a canonical `cdef.key` would add an async
 * dependency and a brief window with no guide context, for a grouping benefit we
 * don't currently need. Revisit if feedback ever needs canonical topic grouping.
 */
export function sceneGuidePage(topic_key: string): Scene {
  return { kind: FEEDBACK_SCENE_KIND.GUIDE_PAGE, topic_key };
}

/** Builds the fallback scene used when no tracked route matches. */
export function sceneUnknown(): Scene {
  return { kind: FEEDBACK_SCENE_KIND.UNKNOWN };
}

/** Builds a document-thread target for feedback opened from a doc surface. */
export function targetDoc(id: PublicDocId): Target {
  return { kind: FEEDBACK_TARGET_KIND.DOC, id };
}

/** Builds a feed-item target for feedback opened from a feed surface. */
export function targetFeedItem(id: PublicDocId): Target {
  return { kind: FEEDBACK_TARGET_KIND.FEED_ITEM, id };
}

/** Builds a product target for feedback opened from a product surface. */
export function targetProduct(product_ref: string): Target {
  return { kind: FEEDBACK_TARGET_KIND.PRODUCT, product_ref };
}

/** Builds a topic target for feedback opened from a guide surface. */
export function targetTopic(topic_key: string): Target {
  return { kind: FEEDBACK_TARGET_KIND.TOPIC, topic_key };
}
